import React, { Fragment, useState, useEffect } from 'react';
import {
  MessageSquare,
  MessageSquareWarning,
  Plus,
  Edit2,
  Trash2,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  X,
  MessageCircle,
  Star,
  Search,
} from 'lucide-react';
import { complaintsApi, usersApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields } from '../utils/dateValidation';

const ORIGIN_LABELS = {
  phone: { label: 'Telefone', icon: Phone },
  email: { label: 'Email', icon: Mail },
  website: { label: 'Site', icon: MessageCircle },
  social_media: { label: 'Redes Sociais', icon: MessageCircle },
  in_person: { label: 'Presencial', icon: MessageCircle },
  letter: { label: 'Carta', icon: Mail },
  regulatory: { label: 'Órgão Regulador', icon: AlertCircle },
};

const TYPE_LABELS = {
  product_quality: 'Qualidade do Produto',
  service: 'Serviço',
  delivery: 'Entrega',
  packaging: 'Embalagem',
  documentation: 'Documentação',
  billing: 'Faturamento',
  other: 'Outros',
};

const PRIORITY_LABELS = {
  low: { label: 'Baixa', color: 'bg-green-500/20 text-green-400' },
  medium: { label: 'Média', color: 'bg-yellow-500/20 text-yellow-400' },
  high: { label: 'Alta', color: 'bg-orange-500/20 text-orange-400' },
  critical: { label: 'Crítica', color: 'bg-red-500/20 text-red-400' },
};

const STATUS_LABELS = {
  open: { label: 'Aberta', color: 'bg-blue-500/20 text-blue-400' },
  analyzing: { label: 'Em Análise', color: 'bg-yellow-500/20 text-yellow-400' },
  action_in_progress: { label: 'Ação em Andamento', color: 'bg-purple-500/20 text-purple-400' },
  pending_response: { label: 'Aguardando Retorno', color: 'bg-orange-500/20 text-orange-400' },
  closed_resolved: { label: 'Resolvida', color: 'bg-green-500/20 text-green-400' },
  closed_unresolved: { label: 'Não Resolvida', color: 'bg-red-500/20 text-red-400' },
};

const SATISFACTION_LABELS = {
  very_satisfied: { label: 'Muito Satisfeito', color: 'text-green-400', stars: 5 },
  satisfied: { label: 'Satisfeito', color: 'text-green-400', stars: 4 },
  neutral: { label: 'Neutro', color: 'text-yellow-400', stars: 3 },
  dissatisfied: { label: 'Insatisfeito', color: 'text-orange-400', stars: 2 },
  very_dissatisfied: { label: 'Muito Insatisfeito', color: 'text-red-400', stars: 1 },
};

const Complaints = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState({ total_year: 0, open: 0, resolved: 0, overdue: 0, satisfaction_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [filters, setFilters] = useState({ status: '', priority: '', complaint_type: '' });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [complaintsRes, statsRes] = await Promise.all([
        complaintsApi.list(filters),
        complaintsApi.stats(),
      ]);
      setComplaints(complaintsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadInteractions = async (complaintId) => {
    try {
      const res = await complaintsApi.listInteractions(complaintId);
      setInteractions(res.data);
    } catch (error) {
      toast.error('Erro ao carregar interações');
    }
  };

  const handleSelectComplaint = async (complaint) => {
    if (detailLoadingId) return;
    setDetailLoadingId(complaint.id);
    try {
      const detailRes = await complaintsApi.get(complaint.id);
      setSelectedComplaint(detailRes.data);
      await loadInteractions(complaint.id);
      setActiveTab('detail');
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const handleCreateComplaint = async (data) => {
    try {
      await complaintsApi.create(data);
      toast.success('Reclamação registrada com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar reclamação'));
    }
  };

  const handleUpdateComplaint = async (data) => {
    try {
      await complaintsApi.update(editingComplaint.id, data);
      toast.success('Reclamação atualizada');
      setShowModal(false);
      setEditingComplaint(null);
      loadData();
      if (selectedComplaint && selectedComplaint.id === editingComplaint.id) {
        const detailRes = await complaintsApi.get(editingComplaint.id);
        setSelectedComplaint(detailRes.data);
      }
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteComplaint = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta reclamação?')) return;
    try {
      await complaintsApi.delete(id);
      toast.success('Reclamação excluída');
      loadData();
      if (selectedComplaint && selectedComplaint.id === id) {
        setSelectedComplaint(null);
        setActiveTab('list');
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleAddInteraction = async (data) => {
    try {
      await complaintsApi.addInteraction(selectedComplaint.id, data);
      toast.success('Interação registrada');
      setShowInteractionModal(false);
      await loadInteractions(selectedComplaint.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao registrar interação'));
    }
  };

  const tabs = [
    { id: 'list', label: 'Reclamações', icon: MessageSquareWarning },
  ];

  return (
    <div className="space-y-6" data-testid="complaints-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <MessageSquare className="text-green-500" />
            Reclamações de Clientes
          </h1>
          <p className="text-gray-400 mt-1">SAC e tratativas de reclamações</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons module="complaints" />
          <button
            onClick={() => { setEditingComplaint(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-complaint-btn"
          >
            <Plus size={20} />
            Nova Reclamação
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total do Ano</p>
          <p className="text-2xl font-bold text-white">{stats.total_year}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Em Aberto</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.open}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Resolvidas</p>
          <p className="text-2xl font-bold text-green-400">{stats.resolved}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Atrasadas</p>
          <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Satisfação</p>
          <p className="text-2xl font-bold text-purple-400">{stats.satisfaction_rate}%</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-green-500 text-green-400'
                : tab.disabled
                ? 'border-transparent text-gray-600 cursor-not-allowed'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading || detailLoadingId ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : activeTab === 'list' ? (
          <ComplaintsTable
            complaints={complaints}
            onSelect={handleSelectComplaint}
            onEdit={(c) => { setEditingComplaint(c); setShowModal(true); }}
            onDelete={handleDeleteComplaint}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <ComplaintDetailView
            complaint={selectedComplaint}
            interactions={interactions}
            onEdit={() => { setEditingComplaint(selectedComplaint); setShowModal(true); }}
            onAddInteraction={() => setShowInteractionModal(true)}
          />
        )}
      </div>

      {/* Complaint Modal */}
      {showModal && (
        <ComplaintModal
          complaint={editingComplaint}
          users={users}
          onClose={() => { setShowModal(false); setEditingComplaint(null); }}
          onSave={editingComplaint ? handleUpdateComplaint : handleCreateComplaint}
        />
      )}

      {/* Interaction Modal */}
      {showInteractionModal && (
        <InteractionModal
          onClose={() => setShowInteractionModal(false)}
          onSave={handleAddInteraction}
        />
      )}
    </div>
  );
};

const ComplaintsTable = ({ complaints, onSelect, onEdit, onDelete, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredComplaints = complaints.filter(c =>
    !searchTerm ||
    (c.code && c.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.customer_name && c.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.subject && c.subject.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (c) => <td className="font-medium text-white font-mono">{c.code}</td> },
    { key: 'customer', label: 'Cliente', render: (c) => (
      <td>
        {c.customer_name}
        {c.customer_company && <span className="text-gray-400 text-xs block">{c.customer_company}</span>}
      </td>
    ) },
    { key: 'subject', label: 'Assunto', render: (c) => <td className="max-w-xs truncate">{c.subject}</td> },
    { key: 'type', label: 'Tipo', render: (c) => <td>{TYPE_LABELS[c.complaint_type] || c.complaint_type}</td> },
    { key: 'priority', label: 'Prioridade', render: (c) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${PRIORITY_LABELS[c.priority]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {PRIORITY_LABELS[c.priority]?.label || c.priority}
        </span>
      </td>
    ) },
    { key: 'status', label: 'Status', render: (c) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[c.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {STATUS_LABELS[c.status]?.label || c.status}
        </span>
      </td>
    ) },
    { key: 'deadline', label: 'Prazo', render: (c) => (
      <td>
        <span className={c.is_overdue ? 'text-red-400 font-semibold' : 'text-gray-400'}>
          {c.deadline_date || '-'}
          {c.is_overdue && <AlertCircle size={14} className="inline ml-1" />}
        </span>
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (c) => (
      <td>
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onEdit(c)} className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg">
            <Edit2 size={18} />
          </button>
          <button onClick={() => onDelete(c.id)} className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'complaints', columns: COLUMNS });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar reclamações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todos os Status</option>
          {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todas Prioridades</option>
          {Object.entries(PRIORITY_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.complaint_type}
          onChange={(e) => setFilters({ ...filters, complaint_type: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todos os Tipos</option>
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <div className="flex items-center">
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredComplaints.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">Nenhuma reclamação encontrada</td>
              </tr>
            ) : (
              filteredComplaints.map((c) => (
                <tr key={c.id} className="cursor-pointer" onClick={() => onSelect(c)}>
                  {visibleColumns.map(col => (
                    <Fragment key={col.key}>{col.render(c)}</Fragment>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ComplaintDetailView = ({ complaint, interactions, onEdit, onAddInteraction }) => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex justify-between items-start">
      <div>
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-semibold text-white">{complaint.code}</h3>
          <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[complaint.status]?.color}`}>
            {STATUS_LABELS[complaint.status]?.label}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs ${PRIORITY_LABELS[complaint.priority]?.color}`}>
            {PRIORITY_LABELS[complaint.priority]?.label}
          </span>
        </div>
        <p className="text-gray-400 mt-1">{complaint.subject}</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onAddInteraction} className="btn-secondary">
          <MessageCircle size={20} />
          Registrar Interação
        </button>
        <button onClick={onEdit} className="btn-primary">
          <Edit2 size={20} />
          Editar
        </button>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Customer Info */}
      <div className="card p-4">
        <h4 className="text-white font-medium mb-3">Dados do Cliente</h4>
        <div className="space-y-2 text-sm">
          <p><span className="text-gray-400">Nome:</span> <span className="text-white">{complaint.customer_name}</span></p>
          {complaint.customer_company && <p><span className="text-gray-400">Empresa:</span> <span className="text-white">{complaint.customer_company}</span></p>}
          {complaint.customer_email && <p><span className="text-gray-400">Email:</span> <span className="text-white">{complaint.customer_email}</span></p>}
          {complaint.customer_phone && <p><span className="text-gray-400">Telefone:</span> <span className="text-white">{complaint.customer_phone}</span></p>}
        </div>
      </div>

      {/* Complaint Info */}
      <div className="card p-4">
        <h4 className="text-white font-medium mb-3">Informações da Reclamação</h4>
        <div className="space-y-2 text-sm">
          <p><span className="text-gray-400">Origem:</span> <span className="text-white">{ORIGIN_LABELS[complaint.origin]?.label}</span></p>
          <p><span className="text-gray-400">Tipo:</span> <span className="text-white">{TYPE_LABELS[complaint.complaint_type]}</span></p>
          {complaint.product_name && <p><span className="text-gray-400">Produto:</span> <span className="text-white">{complaint.product_name}</span></p>}
          {complaint.product_batch && <p><span className="text-gray-400">Lote:</span> <span className="text-white">{complaint.product_batch}</span></p>}
          {complaint.occurrence_date && <p><span className="text-gray-400">Data Ocorrência:</span> <span className="text-white">{complaint.occurrence_date}</span></p>}
          <p><span className="text-gray-400">Recebida em:</span> <span className="text-white">{complaint.receipt_date}</span></p>
          {complaint.deadline_date && <p><span className="text-gray-400">Prazo:</span> <span className={complaint.is_overdue ? 'text-red-400' : 'text-white'}>{complaint.deadline_date}</span></p>}
        </div>
      </div>

      {/* Description */}
      <div className="card p-4 lg:col-span-2">
        <h4 className="text-white font-medium mb-3">Descrição</h4>
        <p className="text-gray-300 text-sm whitespace-pre-wrap">{complaint.description}</p>
      </div>

      {/* Analysis & Actions */}
      {(complaint.root_cause || complaint.immediate_action || complaint.corrective_action) && (
        <div className="card p-4 lg:col-span-2">
          <h4 className="text-white font-medium mb-3">Análise e Ações</h4>
          <div className="space-y-3 text-sm">
            {complaint.root_cause && (
              <div>
                <p className="text-gray-400 font-medium">Causa Raiz:</p>
                <p className="text-gray-300">{complaint.root_cause}</p>
              </div>
            )}
            {complaint.immediate_action && (
              <div>
                <p className="text-gray-400 font-medium">Ação Imediata:</p>
                <p className="text-gray-300">{complaint.immediate_action}</p>
              </div>
            )}
            {complaint.corrective_action && (
              <div>
                <p className="text-gray-400 font-medium">Ação Corretiva:</p>
                <p className="text-gray-300">{complaint.corrective_action}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Response */}
      {complaint.response_to_customer && (
        <div className="card p-4 lg:col-span-2">
          <h4 className="text-white font-medium mb-3">Resposta ao Cliente</h4>
          <p className="text-gray-300 text-sm">{complaint.response_to_customer}</p>
          {complaint.response_date && <p className="text-gray-500 text-xs mt-2">Enviada em: {complaint.response_date}</p>}
          {complaint.customer_satisfaction && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-gray-400">Satisfação:</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={16}
                    className={i <= (SATISFACTION_LABELS[complaint.customer_satisfaction]?.stars || 0) ? SATISFACTION_LABELS[complaint.customer_satisfaction]?.color : 'text-gray-600'}
                    fill={i <= (SATISFACTION_LABELS[complaint.customer_satisfaction]?.stars || 0) ? 'currentColor' : 'none'}
                  />
                ))}
              </div>
              <span className={`text-sm ${SATISFACTION_LABELS[complaint.customer_satisfaction]?.color}`}>
                {SATISFACTION_LABELS[complaint.customer_satisfaction]?.label}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Interactions */}
      <div className="card p-4 lg:col-span-2">
        <h4 className="text-white font-medium mb-3">Histórico de Interações ({interactions.length})</h4>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {interactions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nenhuma interação registrada</p>
          ) : (
            interactions.map((int) => (
              <div key={int.id} className="p-3 bg-gray-700/50 rounded">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${int.direction === 'inbound' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                      {int.direction === 'inbound' ? 'Recebido' : 'Enviado'}
                    </span>
                    <span className="text-gray-400 text-sm">{int.interaction_type}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{new Date(int.created_at).toLocaleString('pt-BR')}</span>
                </div>
                <p className="text-gray-300 text-sm mt-2">{int.summary}</p>
                {int.contact_person && <p className="text-gray-500 text-xs mt-1">Contato: {int.contact_person}</p>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  </div>
);

const ComplaintModal = ({ complaint, users, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    customer_name: complaint?.customer_name || '',
    customer_company: complaint?.customer_company || '',
    customer_email: complaint?.customer_email || '',
    customer_phone: complaint?.customer_phone || '',
    origin: complaint?.origin || 'email',
    complaint_type: complaint?.complaint_type || 'product_quality',
    priority: complaint?.priority || 'medium',
    status: complaint?.status || 'open',
    subject: complaint?.subject || '',
    description: complaint?.description || '',
    product_name: complaint?.product_name || '',
    product_batch: complaint?.product_batch || '',
    occurrence_date: complaint?.occurrence_date || '',
    deadline_date: complaint?.deadline_date || '',
    root_cause: complaint?.root_cause || '',
    immediate_action: complaint?.immediate_action || '',
    corrective_action: complaint?.corrective_action || '',
    response_to_customer: complaint?.response_to_customer || '',
    response_date: complaint?.response_date || '',
    customer_satisfaction: complaint?.customer_satisfaction || '',
    resolution_date: complaint?.resolution_date || '',
    responsible_id: complaint?.responsible_id || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.occurrence_date, label: 'Data da Ocorrência' },
      { value: formData.deadline_date, label: 'Prazo' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    const data = { ...formData };
    if (!data.occurrence_date) delete data.occurrence_date;
    if (!data.deadline_date) delete data.deadline_date;
    if (!data.response_date) delete data.response_date;
    if (!data.resolution_date) delete data.resolution_date;
    if (!data.customer_satisfaction) delete data.customer_satisfaction;
    if (!data.responsible_id) delete data.responsible_id;
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {complaint ? 'Editar Reclamação' : 'Nova Reclamação'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Info */}
          <div className="border-b border-gray-700 pb-4">
            <h4 className="text-white font-medium mb-3">Dados do Cliente</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Empresa</label>
                <input
                  type="text"
                  value={formData.customer_company}
                  onChange={(e) => setFormData({ ...formData, customer_company: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Telefone</label>
                <input
                  type="text"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </div>

          {/* Complaint Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Origem</label>
              <select
                value={formData.origin}
                onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                className="input w-full"
              >
                {Object.entries(ORIGIN_LABELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <select
                value={formData.complaint_type}
                onChange={(e) => setFormData({ ...formData, complaint_type: e.target.value })}
                className="input w-full"
              >
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prioridade</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="input w-full"
              >
                {Object.entries(PRIORITY_LABELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            {complaint && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input w-full"
                >
                  {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Assunto *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Produto</label>
              <input
                type="text"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Lote</label>
              <input
                type="text"
                value={formData.product_batch}
                onChange={(e) => setFormData({ ...formData, product_batch: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data Ocorrência</label>
              <input
                type="date"
                value={formData.occurrence_date}
                onChange={(e) => setFormData({ ...formData, occurrence_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prazo</label>
              <input
                type="date"
                value={formData.deadline_date}
                onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
              <select
                value={formData.responsible_id}
                onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Analysis Section - Only for editing */}
          {complaint && (
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-white font-medium mb-3">Análise e Tratativa</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Causa Raiz</label>
                  <textarea
                    value={formData.root_cause}
                    onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                    className="input w-full"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ação Corretiva</label>
                  <textarea
                    value={formData.corrective_action}
                    onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
                    className="input w-full"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Resposta ao Cliente</label>
                  <textarea
                    value={formData.response_to_customer}
                    onChange={(e) => setFormData({ ...formData, response_to_customer: e.target.value })}
                    className="input w-full"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Data Resposta</label>
                    <input
                      type="date"
                      value={formData.response_date}
                      onChange={(e) => setFormData({ ...formData, response_date: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Satisfação do Cliente</label>
                    <select
                      value={formData.customer_satisfaction}
                      onChange={(e) => setFormData({ ...formData, customer_satisfaction: e.target.value })}
                      className="input w-full"
                    >
                      <option value="">Selecione...</option>
                      {Object.entries(SATISFACTION_LABELS).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const InteractionModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    interaction_type: 'Email',
    direction: 'inbound',
    summary: '',
    contact_person: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Registrar Interação</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <select
                value={formData.interaction_type}
                onChange={(e) => setFormData({ ...formData, interaction_type: e.target.value })}
                className="input w-full"
              >
                <option value="Email">Email</option>
                <option value="Telefone">Telefone</option>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Visita">Visita</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Direção</label>
              <select
                value={formData.direction}
                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                className="input w-full"
              >
                <option value="inbound">Recebido</option>
                <option value="outbound">Enviado</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Pessoa de Contato</label>
            <input
              type="text"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Resumo *</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              className="input w-full"
              rows={4}
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Registrando...</> : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Complaints;
